import { Box, Group, Stack, Text } from '@mantine/core'
import dayjs from 'dayjs'
import { useEffect, useState } from 'react'
import { LuMoon, LuSunrise, LuSunset, LuWaves } from 'react-icons/lu'
import SunCalc from 'suncalc'

interface Props {
  /** Marina latitude. Defaults to Benoa, Bali. */
  lat?: number
  /** Marina longitude. Defaults to Benoa, Bali. */
  lng?: number
  compact?: boolean
}

/**
 * Maritime context widget — sunrise/sunset/golden-hour for the marina's
 * geographic location. Updates once per minute (cheap; just date math).
 *
 * Default coordinates: Benoa Harbor, Bali (-8.748, 115.211) — most common
 * marina in Indonesia. Override via props if you wire in real marina settings.
 *
 * The point: every staff member opens the dashboard and sees information
 * relevant to their job (when can boats safely depart, when does the light go),
 * not abstract metrics. No other admin SaaS shows this.
 */
export function SunriseSunsetWidget({ lat = -8.748, lng = 115.211, compact = false }: Props) {
  // Tick state ensures the widget recomputes around sunrise/sunset transitions
  // without us hand-rolling a timer per event. One minute is plenty for solar.
  const [now, setNow] = useState(() => new Date())
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 60_000)
    return () => clearInterval(id)
  }, [])

  const times = SunCalc.getTimes(now, lat, lng)
  const phase = solarPhase(now, times)
  const fmt = (d: Date) => dayjs(d).format('HH:mm')

  if (compact) {
    return (
      <Group gap="md" wrap="nowrap">
        <Group gap={4} wrap="nowrap">
          <LuSunrise size={13} color="var(--mantine-color-karang-5)" />
          <Text size="xs" c="dimmed">
            {fmt(times.sunrise)}
          </Text>
        </Group>
        <Group gap={4} wrap="nowrap">
          <LuSunset size={13} color="var(--mantine-color-karang-7)" />
          <Text size="xs" c="dimmed">
            {fmt(times.sunset)}
          </Text>
        </Group>
      </Group>
    )
  }

  return (
    <Box>
      <Group gap={4} mb={6}>
        <PhaseIcon phase={phase} />
        <Text size="xs" c="dimmed" tt="uppercase" fw={600} style={{ letterSpacing: '0.08em' }}>
          {phaseLabel(phase)}
        </Text>
      </Group>
      <Group gap="lg" wrap="nowrap">
        <Stack gap={2}>
          <Group gap={6} wrap="nowrap">
            <LuSunrise size={14} color="var(--mantine-color-karang-5)" />
            <Text size="xs" c="dimmed">
              Matahari terbit
            </Text>
          </Group>
          <Text size="sm" fw={600} style={{ fontFamily: 'var(--font-display)' }}>
            {fmt(times.sunrise)}
          </Text>
        </Stack>
        <Stack gap={2}>
          <Group gap={6} wrap="nowrap">
            <LuSunset size={14} color="var(--mantine-color-karang-7)" />
            <Text size="xs" c="dimmed">
              Matahari terbenam
            </Text>
          </Group>
          <Text size="sm" fw={600} style={{ fontFamily: 'var(--font-display)' }}>
            {fmt(times.sunset)}
          </Text>
        </Stack>
        <Stack gap={2}>
          <Group gap={6} wrap="nowrap">
            <LuWaves size={14} color="var(--mantine-color-laut-5)" />
            <Text size="xs" c="dimmed">
              Cahaya emas
            </Text>
          </Group>
          <Text size="sm" fw={600} style={{ fontFamily: 'var(--font-display)' }}>
            {fmt(times.goldenHour)}
          </Text>
        </Stack>
      </Group>
    </Box>
  )
}

type Phase = 'night' | 'dawn' | 'morning' | 'noon' | 'afternoon' | 'goldenHour' | 'dusk'

function solarPhase(now: Date, times: SunCalc.GetTimesResult): Phase {
  const t = now.getTime()
  if (t < times.dawn.getTime()) return 'night'
  if (t < times.sunrise.getTime()) return 'dawn'
  if (t < times.solarNoon.getTime() - 2 * 3600_000) return 'morning'
  if (t < times.solarNoon.getTime() + 2 * 3600_000) return 'noon'
  if (t < times.goldenHour.getTime()) return 'afternoon'
  if (t < times.sunset.getTime()) return 'goldenHour'
  if (t < times.dusk.getTime()) return 'dusk'
  return 'night'
}

function phaseLabel(phase: Phase): string {
  switch (phase) {
    case 'night':
      return 'Malam'
    case 'dawn':
      return 'Subuh'
    case 'morning':
      return 'Pagi'
    case 'noon':
      return 'Siang'
    case 'afternoon':
      return 'Sore'
    case 'goldenHour':
      return 'Cahaya Emas'
    case 'dusk':
      return 'Senja'
  }
}

function PhaseIcon({ phase }: { phase: Phase }) {
  if (phase === 'night' || phase === 'dusk') {
    return <LuMoon size={13} color="var(--mantine-color-langit-6)" />
  }
  if (phase === 'dawn' || phase === 'morning') {
    return <LuSunrise size={13} color="var(--mantine-color-karang-5)" />
  }
  if (phase === 'goldenHour' || phase === 'afternoon') {
    return <LuSunset size={13} color="var(--mantine-color-karang-7)" />
  }
  return <LuWaves size={13} color="var(--mantine-color-laut-5)" />
}
